import React, { useState } from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { ToggleChip } from '../../../ui/ToggleChip/ToggleChip.jsx'

/**
 * MoodCard — chip multi-select (soft cap 3) with per-chip context unfurl.
 * catalog #2, prep-mood. Replaces MoodCompassGrid.
 *
 * Shape: { selected: string[], context: { [mood]: string } }
 *
 * Mood chip vocabulary: pending design-owner sign-off (copy-decisions §open items).
 * Using a provisional set matching "moss=positive, rust=hard, mustard=stuck" design intent.
 */

const MOOD_CHIPS = [
  { id: 'energised',  label: 'energised',  tone: 'moss' },
  { id: 'focused',    label: 'focused',    tone: 'olive' },
  { id: 'anxious',    label: 'anxious',    tone: 'mustard' },
  { id: 'stuck',      label: 'stuck',      tone: 'mustard' },
  { id: 'frustrated', label: 'frustrated', tone: 'rust' },
  { id: 'drained',    label: 'drained',    tone: 'rust' },
  { id: 'content',    label: 'content',    tone: 'clay' },
  { id: 'curious',    label: 'curious',    tone: 'berry' },
]

const SOFT_CAP = 3

export function MoodCard({ card, value, onChange, disabled }) {
  const selected = value?.selected ?? []
  const context = value?.context ?? {}

  function toggle(id) {
    if (disabled) return
    const isSelected = selected.includes(id)
    if (isSelected) {
      const next = selected.filter((s) => s !== id)
      const nextCtx = { ...context }
      delete nextCtx[id]
      onChange({ selected: next, context: nextCtx })
    } else {
      if (selected.length >= SOFT_CAP) return
      onChange({ selected: [...selected, id], context })
    }
  }

  function setContext(id, text) {
    onChange({ selected, context: { ...context, [id]: text } })
  }

  const isOver = selected.length > SOFT_CAP

  return (
    <CardFrame
      kind="prep"
      icon="◉"
      iconClass="berry"
      title="Mood"
      prompt="How do you feel going in?"
      state={disabled ? 'locked' : isOver ? 'error' : selected.length > 0 ? 'filled' : 'empty'}
      data-testid="prep-card-prep-mood"
      className="prepcard"
    >
      <div style={styles.wrap}>
        <div style={styles.chips} role="group" aria-label="Mood chips">
          {MOOD_CHIPS.map((chip) => {
            const isSelected = selected.includes(chip.id)
            return (
              <ToggleChip
                key={chip.id}
                selected={isSelected}
                disabled={disabled || (!isSelected && selected.length >= SOFT_CAP)}
                onClick={() => toggle(chip.id)}
                aria-pressed={isSelected}
              >
                {chip.label}
              </ToggleChip>
            )
          })}
        </div>
        {selected.length > 0 && (
          <div style={styles.contexts}>
            {selected.map((id) => {
              const chip = MOOD_CHIPS.find((c) => c.id === id)
              return (
                <div key={id} style={styles.contextRow}>
                  <span style={styles.contextLabel}>{chip?.label}</span>
                  <input
                    type="text"
                    value={context[id] ?? ''}
                    placeholder="anything behind this?"
                    disabled={disabled}
                    onChange={(e) => setContext(id, e.target.value)}
                    style={styles.contextInput}
                    aria-label={`Context for ${chip?.label}`}
                    maxLength={120}
                  />
                </div>
              )
            })}
          </div>
        )}
        {isOver && (
          <p style={styles.overMsg} role="alert">Choose up to {SOFT_CAP} moods</p>
        )}
      </div>
    </CardFrame>
  )
}

const styles = {
  wrap: { padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  contexts: { display: 'flex', flexDirection: 'column', gap: 8 },
  contextRow: { display: 'flex', alignItems: 'center', gap: 8 },
  contextLabel: { fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--color-text-muted)', minWidth: 68, textTransform: 'uppercase', letterSpacing: '0.04em' },
  contextInput: { flex: 1, border: 'none', borderBottom: '1px solid var(--color-line)', outline: 'none', background: 'transparent', fontSize: 12, color: 'var(--color-text-primary)', fontFamily: 'inherit', padding: '2px 0' },
  overMsg: { fontSize: 11, color: 'var(--color-danger-text)', fontStyle: 'italic' },
}
